// Adapted from https://github.com/Azure-Samples/ms-identity-b2c-javascript-spa
// Create the main msalAgent instance
// configuration parameters are located at authConfig.js
import axios from 'axios'
import b2cPolicies from '@/auth/policies'
import * as msal from '@azure/msal-browser'
import authConfig from '@/auth/authConfig'
import {useMsalStore} from '@/store/msal'
import {useApiStore} from '@/store/api'
const msalAgent = new msal.PublicClientApplication(authConfig.msalConfig)
const msalStore = useMsalStore();

let accountId = ""

function setAccount(account) {
    accountId = account.homeAccountId
}

function selectAccount() {
    /**
     * See here for more info on account retrieval:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
     */

    const currentAccounts = msalAgent.getAllAccounts()

    if (currentAccounts.length < 1) {
        return
    } else if (currentAccounts.length > 1) {

        /**
         * Due to the way MSAL caches account objects, the auth response from initiating a user-flow
         * is cached as a new account, which results in more than one account in the cache. Here we make
         * sure we are selecting the account with homeAccountId that contains the sign-up/sign-in user-flow,
         * as this is the default flow the user initially signed-in with.
         */
        const accounts = currentAccounts.filter(account =>
            account.homeAccountId.toUpperCase().includes(b2cPolicies.names.signUpSignIn.toUpperCase())
            &&
            account.idTokenClaims.iss.toUpperCase().includes(b2cPolicies.authorityDomain.toUpperCase())
            &&
            account.idTokenClaims.aud === authConfig.msalConfig.auth.clientId
            )

        if (accounts.length > 1) {
            // localAccountId identifies the entity for which the token asserts information.
            if (accounts.every(account => account.localAccountId === accounts[0].localAccountId)) {
                // All accounts belong to the same user
                setAccount(accounts[0])
            } else {
                // Multiple users detected. Logout all to be safe.
                signOut()
            }
        } else if (accounts.length === 1) {
            setAccount(accounts[0])
        }

    } else if (currentAccounts.length === 1) {
        setAccount(currentAccounts[0])
    }
}

// in case of page refresh
selectAccount()

function handleResponse(response) {
    /**
     * To see the full list of response object properties, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#response
     */

    if (response !== null) {
        setAccount(response.account)
    } else {
        selectAccount()
    }
}

function signIn() {
    /**
     * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
     */

     msalAgent.loginPopup(authConfig.loginRequest)
        .then(handleResponse)
        .catch(error => {
            console.log(error)
        })
}

function signOut() {

    /**
     * You can pass a custom request object below. This will override the initial configuration. For more information, visit:
     * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/request-response-object.md#request
     */

    const logoutRequest = {
        postLogoutRedirectUri: authConfig.msalConfig.auth.redirectUri + '?signout=true',
        mainWindowRedirectUri: authConfig.msalConfig.auth.redirectUri + '?signout=true'
    }

    msalAgent.logoutPopup(logoutRequest)
}

function getTokenPopup(request) {

    /**
    * See here for more information on account retrieval:
    * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-common/docs/Accounts.md
    */
    request.account = msalAgent.getAccountByHomeId(accountId)

    /**
     *
     */
    return msalAgent.acquireTokenSilent(request)
        .then(async (response) => {
            // In case the response from B2C server has an empty accessToken field
            // throw an error to initiate token acquisition
            if (!response.accessToken || response.accessToken == "") {
                throw new msal.InteractionRequiredAuthError
            }
            // update last login
            await updateIdentityProvider(response)
            console.log('last_identity_provider updated')
            return response // success
        })
        .catch(error => {
            console.error(error)
            if (error instanceof msal.InteractionRequiredAuthError) {
                // fallback to interaction when silent call fails
                console.warn('Silent token acquisition fails. Acquiring token using popup.')
                return msalAgent.acquireTokenPopup(request)
                    .then(response => {
                        if (!response.accessToken || response.accessToken == "") {
                            console.warn('Popup token acquisition fails. Aborting API access. \n')
                        } else {
                            return response // second try success
                        }
                    }).catch(error => {
                        console.error(error)
                        console.warn('Popup token acquisition fails. Aborting API access. \n')
                        signOut()
                        signIn()
                    });
            }
        });
}

async function passTokenToApi() {
    await getTokenPopup(authConfig.tokenRequest)
        .then(response => {
            if (response) {
                if (!response.accessToken || '' == response.accessToken) {
                    return
                }
                console.log('access_token acquired at: ' + new Date().toString())
                try {
                    msalStore.setAccessToken(response.accessToken)

                } catch (error) {
                    console.error(error)
                }
                return true
            }
        }).catch((error) => {
            console.error(error)
            return false
        });
}

/**
 * To initiate a B2C user-flow, simply make a login request using
 * the full authority string of that user-flow e.g.
 * https://fabrikamb2c.b2clogin.com/fabrikamb2c.onmicrosoft.com/B2C_1_edit_profile_v2
 */
async function editProfile() {

    const editProfileRequest = b2cPolicies.authorities.editProfile;
    editProfileRequest.loginHint = msalAgent.getAccountByHomeId(accountId).username;

    await msalAgent.loginPopup(editProfileRequest)
        .catch(error => {
            console.error(error)
        });
}

async function updateIdentityProvider(userData) {
    // async call to api to set the last_identity_provider on the attendee that just logged in
    let apiStore = useApiStore()

    let identity_provider;
    if( userData.account.idTokenClaims.currentUserIdentities.length > 0){
        let identity_provider_raw = userData.account.idTokenClaims.currentUserIdentities[0].Issuer
        switch (true) {
            case /microsoft/g.test(identity_provider_raw):
                identity_provider = 'microsoft'
                break;
            case /google/g.test(identity_provider_raw):
                identity_provider = 'google'
                break;
            case /apple/g.test(identity_provider_raw):
                identity_provider = 'apple'
                break;
            default:
                identity_provider = 'unknown'
        }
    } else {
        identity_provider = 'local'
    }

    const endpoint = apiStore.buildUrl('/events/attendees/')
    const data = {last_identity_provider: identity_provider, id: userData.uniqueId}
    const headers = {Authorization: `Bearer ${userData.accessToken}`}
    await axios.patch(endpoint, data, {headers: headers}).catch((error) => console.error(error.response))
}

export {editProfile, getTokenPopup, msalAgent, passTokenToApi, signIn, signOut}
